import React from "react";
import { useQueryClient } from "@tanstack/react-query";

import type { Message } from "types/Message";
import { waitForStreamEnd } from "services/cortevaChain/hooks/activeStreams";
import useCommitMessages, {
  type UseCommitMessagesArgs,
} from "./useCommitMessages";

/**
 * Returns `removeMessagesAfterStream(threadId, shouldRemove)` (ADR 071). It
 * waits for any streaming reply to end, because the reply's next chunk would
 * restore removed messages, then commits the cached messages minus the
 * matching ones. Resolves to whether any matched. Never rejects.
 */
export default function useRemoveMessagesAfterStream(
  writeErrorText: UseCommitMessagesArgs
) {
  const queryClient = useQueryClient();
  const commitMessages = useCommitMessages(writeErrorText);

  return React.useCallback(
    async (
      threadId: string,
      shouldRemove: (message: Message) => boolean
    ): Promise<boolean> => {
      await waitForStreamEnd(threadId);
      const messages =
        queryClient.getQueryData<{ messages: Message[] }>([
          "cc-db-api",
          "threads",
          threadId,
          "messages",
        ])?.messages ?? [];
      const remainingMessages = messages.filter(
        (message) => !shouldRemove(message)
      );
      if (remainingMessages.length === messages.length) return false;

      await commitMessages(threadId, remainingMessages);
      return true;
    },
    [queryClient, commitMessages]
  );
}
